import { type Dispatch, type SetStateAction } from 'react';
import { mynazcaRouteCopy, mynazcaT, type MynazcaLang } from '../apps/mynazca/mynazcaRouteCopy';
import { type ImagePathOptions } from '../apps/mynazca/mynazcaWorkers';
import { MyNazcaRangeField } from './MyNazcaRangeField';

type RouteMode = 'strokeTrace' | 'density';

type MyNazcaRouteOptionsFieldsProps = {
  lang: MynazcaLang;
  busy: string | null;
  workingBlob: Blob | null;
  options: ImagePathOptions;
  setOptions: Dispatch<SetStateAction<ImagePathOptions>>;
  loadSavedOptionsForMode: (mode: RouteMode) => ImagePathOptions;
  onCreatePathPreview: () => void;
  showCreateButton?: boolean;
};

const ROUTE_MODES = [
  { id: 'strokeTrace' as const, label: mynazcaRouteCopy.modeLine },
  { id: 'density' as const, label: mynazcaRouteCopy.modePhoto },
];

export function MyNazcaRouteOptionsFields({
  lang,
  busy,
  workingBlob,
  options,
  setOptions,
  loadSavedOptionsForMode,
  onCreatePathPreview,
  showCreateButton = true,
}: MyNazcaRouteOptionsFieldsProps) {
  const activeMode: RouteMode = options.routeMode === 'density' ? 'density' : 'strokeTrace';

  const selectMode = (mode: RouteMode) => {
    const saved = loadSavedOptionsForMode(mode);
    setOptions((o) => ({ ...o, ...saved }));
  };

  return (
    <div className="mynazca-app__route-options-stack">
      <div className="mynazca-app__route-mode-shell">
        <div
          className="mynazca-app__route-mode-tabs"
          role="tablist"
          aria-label={mynazcaT(mynazcaRouteCopy.tablistLabel, lang)}
        >
          {ROUTE_MODES.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              role="tab"
              id={`mynazca-route-mode-tab-${id}`}
              aria-selected={activeMode === id}
              aria-controls={`mynazca-route-mode-panel-${id}`}
              className={`mynazca-app__route-mode-tab${activeMode === id ? ' mynazca-app__route-mode-tab--active' : ''}`}
              onClick={() => selectMode(id)}
              disabled={busy != null}
            >
              {mynazcaT(label, lang)}
            </button>
          ))}
        </div>

        <div
          id={`mynazca-route-mode-panel-${activeMode}`}
          role="tabpanel"
          aria-labelledby={`mynazca-route-mode-tab-${activeMode}`}
          className="mynazca-app__route-mode-panel"
        >
        <MyNazcaRangeField
          label={lang === 'ja' ? '画像の大きさ' : 'Max size'}
          min={0}
          max={1000}
          step={100}
          value={options.maxSize ?? 0}
          disabled={busy != null}
          formatValue={(v) => (v === 0 ? (lang === 'ja' ? '原寸' : 'orig') : String(v))}
          hint={
            lang === 'ja'
              ? '処理前に画像を縮小します。0は原寸のまま。大きいほど精細ですが処理が遅くなります。'
              : 'Shrink the image before processing. 0 keeps the original size. Larger = finer but slower.'
          }
          onChange={(maxSize) => setOptions((o) => ({ ...o, maxSize }))}
        />

        {activeMode === 'density' && (
          <>
            <MyNazcaRangeField
              label={lang === 'ja' ? 'エッジ感度（低）' : 'Edge (low)'}
              min={10}
              max={255}
              value={options.thresholdLow ?? 50}
              disabled={busy != null}
              hint={
                lang === 'ja'
                  ? '小さくすると細かい輪郭まで拾います。線が多すぎるときは上げてください。'
                  : 'Smaller = picks up finer outlines. Raise if there are too many lines.'
              }
              onChange={(thresholdLow) => setOptions((o) => ({ ...o, thresholdLow }))}
            />
            <MyNazcaRangeField
              label={lang === 'ja' ? 'エッジ感度（高）' : 'Edge (high)'}
              min={50}
              max={255}
              value={options.thresholdHigh ?? 255}
              disabled={busy != null}
              hint={
                lang === 'ja'
                  ? '大きくするとはっきりした輪郭だけ残します。ノイズが多いときは上げてください。'
                  : 'Larger = keeps only clear outlines. Raise to reduce noise.'
              }
              onChange={(thresholdHigh) => setOptions((o) => ({ ...o, thresholdHigh }))}
            />
          </>
        )}

        {activeMode === 'strokeTrace' && (
          <MyNazcaRangeField
            label={lang === 'ja' ? 'インクしきい値' : 'Ink threshold'}
            min={50}
            max={250}
            value={options.inkThreshold ?? 200}
            disabled={busy != null}
            hint={
              lang === 'ja'
                ? 'この値より暗いピクセルをインク（線）とみなします。値を上げると薄い線も拾います。'
                : 'Pixels darker than this are treated as ink. Raise to pick up lighter lines.'
            }
            onChange={(inkThreshold) => setOptions((o) => ({ ...o, inkThreshold }))}
          />
        )}

        <MyNazcaRangeField
          label={lang === 'ja' ? 'パスの点数' : 'Max points'}
          min={200}
          max={3500}
          step={100}
          value={options.maxPoints ?? 1500}
          disabled={busy != null}
          hint={
            lang === 'ja'
              ? '多いほど細かい絵が描けます。少ないと処理が速くなりますが、形が粗くなります。'
              : 'More points = finer drawing. Fewer = faster but rougher shapes.'
          }
          onChange={(maxPoints) => setOptions((o) => ({ ...o, maxPoints }))}
        />
        </div>
      </div>

      {showCreateButton && (
        <div className="mynazca-app__route-create-actions">
          <button
            type="button"
            className="mynazca-app__btn-primary mynazca-app__btn-primary--block"
            onClick={() => onCreatePathPreview()}
            disabled={busy != null || workingBlob == null}
          >
            {mynazcaT(mynazcaRouteCopy.createButton, lang)}
          </button>
        </div>
      )}
    </div>
  );
}
